// src/components/Common/InterestRatesModal.jsx

import React, { useEffect, useMemo, useState } from "react";
import { getDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";
import { CommonTable } from "../../shared/ui/table/CommonTable.jsx";
import { LineChartWithToggles } from "../../shared/ui/chart/LineChartWithToggles.jsx";
import { XMarkIcon } from "@heroicons/react/24/solid";

/**
 * Canonical source for CPI-U annual average percent change (calendar year).
 * BLS “CPI Supplemental Files” (Table 24 is commonly used).
 */
const BLS_CPI_SUPPLEMENTAL_URL =
  "https://www.bls.gov/cpi/tables/supplemental-files/home.htm";

/**
 * @typedef {Object} InterestRateRow
 * @property {string} year - Year label (kept as a string for display).
 * @property {number} yearNum - Numeric year used strictly for sorting.
 * @property {number} rate - Inflation/interest rate value as a number (e.g., 3.2 for 3.2%).
 */

/**
 * Convert a Firestore "years" object into a normalized, sorted table row array.
 *
 * Input format is expected to be:
 *   { "2024": 3.2, "2023": 4.1, ... }
 *
 * Rules:
 * - Coerces year keys to numbers for sorting but preserves year as a string for display.
 * - Coerces rate values to numbers where possible.
 * - Filters invalid entries.
 * - Sorts descending by year.
 *
 * @param {Record<string, number|string>|undefined|null} yearsObj
 * @returns {InterestRateRow[]}
 */
function toRows(yearsObj) {
  const entries = Object.entries(yearsObj || {});
  return entries
    .map(([year, rate]) => {
      const yearNum = Number(year);
      const rateNum = typeof rate === "number" ? rate : Number(rate);

      return {
        year: String(year),
        yearNum,
        rate: rateNum,
      };
    })
    .filter((r) => Number.isFinite(r.yearNum) && Number.isFinite(r.rate))
    .sort((a, b) => b.yearNum - a.yearNum);
}

/**
 * Format a number as a 1-decimal percentage string.
 *
 * @param {number|string|undefined|null} v
 * @returns {string}
 */
function fmtPercent(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(1)}%`;
}

/**
 * @typedef {Object} DocPath
 * @property {string} collection - Firestore collection name.
 * @property {string} id - Firestore document id.
 */

/**
 * Modal that displays inflation ("interest rate") values by year.
 *
 * Data source:
 * - Reads a Firestore doc that contains `{ years: { [year: string]: number } }`.
 * - The values are intended to represent CPI-U annual average percent change (calendar year).
 *
 * UI:
 * - Renders a CommonTable of year/rate.
 * - Optionally renders a simple line chart below the table using LineChartWithToggles.
 * - Provides BLS source definition link.
 *
 * Accessibility:
 * - Uses `role="dialog"` and `aria-modal="true"`.
 * - Closes on backdrop click and Escape.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is visible.
 * @param {() => void} props.onClose - Called when the modal should close.
 * @param {string} [props.title="Inflation Rates by Year"] - Header title displayed in the modal.
 * @param {DocPath} [props.docPath] - Firestore document location for the years map.
 * @returns {JSX.Element|null}
 */
export function InterestRatesModal({
  open,
  onClose,
  title = "Inflation Rates by Year",
  docPath = { collection: "common", id: "inflationByYear" },
}) {
  /** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
  const [loading, setLoading] = useState(false);

  /**
   * Raw Firestore map:
   *   { "2024": 3.2, "2023": 4.1, ... }
   *
   * @type {[Record<string, number|string>, React.Dispatch<React.SetStateAction<Record<string, number|string>>>]}
   */
  const [years, setYears] = useState({});

  /** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */
  const [error, setError] = useState("");

  /**
   * Close modal on Escape key.
   */
  useEffect(() => {
    if (!open) return;

    /**
     * @param {KeyboardEvent} e
     */
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  /**
   * Load inflation rates from Firestore when opened (or when docPath changes).
   * Uses an "alive" guard to prevent state updates after unmount/close.
   */
  useEffect(() => {
    if (!open) return;

    let alive = true;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const snap = await getDoc(doc(db, docPath.collection, docPath.id));
        if (!alive) return;

        if (!snap.exists()) {
          setYears({});
          setError(`Document not found: ${docPath.collection}/${docPath.id}`);
          return;
        }

        const data = snap.data();
        setYears(data?.years || {});
      } catch (e) {
        console.error("Failed to load inflation rates:", e);
        if (alive) setError("Failed to load rates from the database.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [open, docPath.collection, docPath.id]);

  /**
   * Table rows (sorted newest -> oldest).
   *
   * @type {InterestRateRow[]}
   */
  const rows = useMemo(() => toRows(years), [years]);

  /**
   * Chart model derived from rows.
   * Chart uses chronological labels (oldest -> newest) for a natural left-to-right trend.
   *
   * @returns {{labels: string[], series: Array<Object>}}
   */
  const chart = useMemo(() => {
    if (!rows?.length) return { labels: [], series: [] };

    const asc = [...rows].sort((a, b) => a.yearNum - b.yearNum);
    const labels = asc.map((r) => r.year);

    const inflation = asc.map((r) => {
      const v = Number(r.rate);
      return Number.isFinite(v) ? v : null;
    });

    return {
      labels,
      series: [
        {
          key: "inflation",
          label: "Inflation (CPI-U, annual avg % change)",
          data: inflation,
          valueType: "percent",
          group: "other",
          defaultVisible: true,
        },
      ],
    };
  }, [rows]);

  /**
   * Column definitions for CommonTable.
   *
   * @type {Array<{
   *  key: string,
   *  label: string,
   *  align?: "left"|"center"|"right",
   *  style?: React.CSSProperties,
   *  render?: (row: any) => React.ReactNode
   * }>}
   */
  const columns = useMemo(
    () => [
      {
        key: "year",
        label: "Year",
        align: "center",
        style: { width: "120px", minWidth: "120px" },
      },
      {
        key: "rate",
        label: "Rate",
        align: "center",
        render: (row) => fmtPercent(row?.rate),
        style: { width: "140px", minWidth: "140px" },
      },
    ],
    []
  );

  /**
   * Guard used to avoid rendering chart scaffolding until we have valid chart inputs.
   */
  const showChart = chart.labels.length > 0 && chart.series.length > 0;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center"
      aria-modal="true"
      role="dialog"
    >
      {/* Backdrop (click closes) */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative z-[9999] w-[min(760px,92vw)] max-h-[85vh] overflow-auto rounded-lg bg-white shadow-xl border border-gray-200">
        {/* Header: title + close share the same vertical center */}
        <div className="px-4 border-b border-gray-200 h-14 flex items-center">
          {/* Left spacer matches close button size so title stays truly centered */}
          <div className="h-10 w-10" />

          <div className="flex-1 text-center">
            <div className="text-lg font-semibold text-gray-900 leading-none">
              {title}
            </div>
          </div>

          <button
            type="button"
            className="h-10 w-10 inline-flex items-center justify-center rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            onClick={onClose}
            aria-label="Close modal"
            title="Close"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {loading ? (
            <div className="text-sm text-gray-600">Loading rates…</div>
          ) : error ? (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
              {error}
            </div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-gray-600">No rate data found.</div>
          ) : (
            <>
              <CommonTable
                title={null}
                columns={columns}
                data={rows}
                isEditing={false}
                headerColor="bg-[#e5eef9]"
                showAggregateTotal={false}
                defaultSortField={null}
                defaultSortOrder="asc"
              />

              {showChart && (
                <LineChartWithToggles
                  labels={chart.labels}
                  series={chart.series}
                  title="Inflation Trend"
                  showSeriesToggles={false}
                  showChartTypeSelector={false}
                  showTotalWhenNoneSelected={false}
                  proposedRenderMode="overlay"
                />
              )}

              {showChart && (
                <div className="text-[11px] text-gray-500">
                  Source (definition):{" "}
                  <a
                    href={BLS_CPI_SUPPLEMENTAL_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-700 hover:text-blue-900 underline"
                    title="BLS CPI supplemental tables (CPI-U annual average percent changes)"
                  >
                    U.S. Bureau of Labor Statistics CPI supplemental tables
                  </a>{" "}
                  (commonly Table 24, CPI-U annual average % change)
                </div>
              )}
            </>
          )}

          {!loading && !error && (
            <div className="text-[11px] text-gray-500">
              Note: These rates are intended to represent CPI-U annual average
              percent change (calendar year). This is different from
              December-to-December CPI.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
